import { el, list, mount, unmount, setChildren, setAttr } from 'redom';

import Sortable from 'sortablejs';

import * as serializer from 'basic-editor-value-serializer';

import application from 'application';
import serverInterface from 'server-interface';

import formParentEntityTypes from 'constants/form-parent-entity-types';

import SimpleDialogNative from 'generics/components/simple-dialog/native';
import AddNewItemButton from 'generics/components/add-new-item-buttom';
import SaveButton from 'generics/components/save-button';

import {
  getVisualStyles
} from '../../../utilities';

import fieldTypes from './field-types';

import FormFieldRow from './field-row';
import FieldRowEditor from './editors';
import FormDetailsEditor from './details-editor';

import {
  getDefaultOptionsByType
} from './editors/utilities';

import {
  determineFieldLabel
} from './utilities';

class FormEditor {

  constructor(configuration) {

    this.pageId = configuration.pageId;
    this.formId = configuration.formId;
    this.page = configuration.page;
    this.name = configuration.name;
    this.submitButtonLabel = configuration.submitButtonLabel;
    this.successMessage = configuration.successMessage;
    this.apiEndpoint = configuration.apiEndpoint;
    this.forwardAuthorizationToken = configuration.forwardAuthorizationToken;
    this.apiResponseHandler = configuration.apiResponseHandler;
    this.notify = configuration.notify;
    this.fieldsOrder = [];
    this.fieldRows = structuredClone(configuration.fieldRows);
    this.onChange = configuration.onChange;
    this.signupForm = configuration.properties.signupForm;

    this.submitButton = null;
    this.staticElementsContainer = null;
    this.fieldRowsContainer = null;
    this.fieldRowEditorDialog = null;
    this.formDetailsEditorDialog = null;
    this.dynamicElementsContainer = null;
    this.sortable = null;
    this.style = el('style');

    this.el = el('.form-editor-main', {
      style: getVisualStyles(configuration.properties)
    }, [
      this.style,
      this.renderStaticElements(),
      this.renderDynamicElementsContainer()
    ]);

    if (this.signupForm === true) {
      this.el.classList.add('signup-form');
    }

  }

  onmount() {

    this.initialize();

  }

  onunmount() {

    this.sortable?.destroy();

  }

  renderStaticElements() {

    this.staticElementsContainer = el('div.form-editor-static-elements', [
      this.renderFormFields(),
      this.renderFormButtonControls()
    ]);

    return this.staticElementsContainer;

  }

  handleSortEnd(event) {
    serverInterface.startup.forms.setFormFieldsOrder(this.formId, this.sortable.toArray());
    this.propogateChanges();
  }

  renderFormFields() {

    this.fieldRowsContainer = list('div.form-editor-field-rows', FormFieldRow, null, this);

    this.sortable = new Sortable(this.fieldRowsContainer.el, {
      onEnd: this.handleSortEnd.bind(this)
    });

    this.doUpdateFieldRows();

    return this.fieldRowsContainer;

  }

  renderAddFieldControl() {

    return el('.form-editor-add-field-control-container', new AddNewItemButton({
      label: 'Add field',
      onClick: this.handleAddFieldRowButtonClick.bind(this)
    }));

  }

  renderSubmitFormControl() {

    this.submitButton = new SaveButton({
      label: this.submitButtonLabel || 'Submit',
      onClick: this.handleSubmitButtonClick.bind(this)
    });

    return el('.form-editor-submit-control-container', this.submitButton);

  }

  renderFormButtonControls() {

    return el('.form-editor-button-controls-container', [
      this.renderAddFieldControl(),
      this.renderSubmitFormControl()
    ]);

  }

  renderDynamicElementsContainer() {

    this.dynamicElementsContainer = el('div.form-editor-dynamic-elements');

    return this.dynamicElementsContainer;

  }

  getDefaultFieldRow() {

    const defaultFieldRow = {
      label: determineFieldLabel(this.fieldRows),
      type: fieldTypes.TEXT,
      options: {
        required: false
      }
    };

    return defaultFieldRow;

  }

  doUpdateFieldRows() {

    const fieldsOrder = this.fieldsOrder;
    const sortedRows = (this.fieldRows || []).sort((a, b) => fieldsOrder.indexOf(a._id) - fieldsOrder.indexOf(b._id));

    this.fieldRowsContainer.update(sortedRows);

  }

  propogateChanges() {

    this.onChange({
      formId: this.formId,
      lastUpdatedAt: Date.now()
    });

  }

  updateFieldRows() {

    this.doUpdateFieldRows();

    this.propogateChanges();

  }

  async addNewFieldRow() {

    const defaultFieldRow = this.getDefaultFieldRow();

    const field = await serverInterface.startup.forms.createFormField(this.formId, defaultFieldRow);

    this.fieldRows = [...this.fieldRows, field];

    this.fieldsOrder.push(field._id);

    this.updateFieldRows();

    this.handleEditFieldRow(this.fieldRows.length - 1);

  }

  handleAddFieldRowButtonClick() {

    this.addNewFieldRow();

  }

  async saveFormDetails(data) {

    await serverInterface.startup.forms.updateFormById(this.formId, data);

    this.name = data.name;
    this.submitButtonLabel = data.submitButtonLabel;
    this.successMessage = data.successMessage;
    this.apiEndpoint = data.apiEndpoint;
    this.forwardAuthorizationToken = data.forwardAuthorizationToken;
    this.apiResponseHandler = data.apiResponseHandler;
    this.notify = data.notify;

    this.submitButton.update({ label: this.submitButtonLabel });

    this.propogateChanges();

  }

  handleSubmitButtonClick() {

    const self = this;
    const fieldRowEditorOptions = {
      data: {
        page: this.page,
        name: this.name,
        submitButtonLabel: this.submitButtonLabel,
        successMessage: this.successMessage,
        apiEndpoint: this.apiEndpoint,
        forwardAuthorizationToken: this.forwardAuthorizationToken,
        apiResponseHandler: this.apiResponseHandler,
        notify: this.notify
      },
      async onSave(data, dialogConfiguration) {
        await self.saveFormDetails(data);
        dialogConfiguration.onComplete();
      }
    };

    application.dialogsManager.display(FormDetailsEditor, fieldRowEditorOptions, {
      contentHolderClassName: 'form-editor-field-row-editor',
    });

  }

  showFormDetailsEditor() {

    mount(this.dynamicElementsContainer, this.formDetailsEditorDialog);

  }

  hideFormDetailsEditor() {

    unmount(this.dynamicElementsContainer, this.formDetailsEditorDialog);

    this.formDetailsEditorDialog = null;

  }

  showFieldRowEditor() {

    mount(this.dynamicElementsContainer, this.fieldRowEditorDialog);

  }

  hideFieldRowEditor() {

    unmount(this.dynamicElementsContainer, this.fieldRowEditorDialog);

    this.fieldRowEditorDialog = null;

  }

  async updateFieldRowByIndex(fieldRowIndex, fieldRow) {

    this.fieldRows = this.fieldRows.map((row, index) => {
      if (index === fieldRowIndex) {
        return fieldRow;
      }
      return row;
    });

    const data = {
      ...fieldRow
    };

    delete data._id;

    await serverInterface.startup.forms.updateFormFieldById(this.formId, fieldRow._id, data);

    this.updateFieldRows();

  }

  async deleteFieldRowByIndex(fieldRowIndex) {

    const rows = this.fieldRows.splice(fieldRowIndex, 1);

    const fieldId = rows[0]._id;

    this.fieldsOrder.splice(this.fieldsOrder.indexOf(fieldId), 1);

    await serverInterface.startup.forms.deleteFormFieldById(this.formId, fieldId);

    this.updateFieldRows();

  }

  handleEditFieldRow(fieldRowIndex) {

    const self = this;
    const fieldRowEditorOptions = {
      fieldRowIndex,
      fieldRow: this.fieldRows[fieldRowIndex],
      signupForm: this.signupForm,
      onSave(fieldRowIndex, fieldRow, dialogConfiguration) {
        self.updateFieldRowByIndex(fieldRowIndex, fieldRow);
        dialogConfiguration.onComplete();
      },
      onDelete(fieldRowIndex, dialogConfiguration) {
        self.deleteFieldRowByIndex(fieldRowIndex);
        dialogConfiguration.onComplete();
      }
    };

    application.dialogsManager.display(FieldRowEditor, fieldRowEditorOptions, {
      contentHolderClassName: 'form-editor-field-row-editor'
    });

  }

  async createForm() {

    const form = await serverInterface.startup.forms.createForm({
      parentEntityId: this.pageId,
      parentEntityType: formParentEntityTypes.PAGE
    });

    this.formId = form._id;
    this.name = form.name;
    this.apiResponseHandler = form.apiResponseHandler;
    this.redirectPath = form.redirectPath;
    this.fieldsOrder = form.fieldsOrder;

  }

  async cloneForm() {

    const form = await serverInterface.startup.pages.cloneForm(this.pageId, this.cloneFrom);

    this.formId = form._id;

    this.fieldRows = structuredClone(form.fields);
    this.apiResponseHandler = form.apiResponseHandler;
    this.redirectPath = form.redirectPath;

  }

  async createFirstField() {

    let type = fieldTypes.TEXT;

    if (this.signupForm === true) {
      type = fieldTypes.EMAIL;
    }

    const field = await serverInterface.startup.forms.createFormField(this.formId, {
      label: 'Sample label',
      type: type,
      options: getDefaultOptionsByType(type)
    });

    this.fieldRows = [field];

  }

  async tryCreateForm() {

    if (this.formId !== null) {
      return;
    }

    await this.createForm();
    await this.createFirstField();

    this.updateFieldRows();

  }

  async tryLoadForm() {

    if (this.formId === null) {
      return;
    }

    const form = await serverInterface.startup.forms.getFormById(this.formId);

    this.name = form.name;
    this.submitButtonLabel = form.submitButtonLabel;
    this.successMessage = form.successMessage;
    this.apiEndpoint = form.apiEndpoint;
    this.forwardAuthorizationToken = form.forwardAuthorizationToken;
    this.notify = form.notify;
    this.apiResponseHandler = form.apiResponseHandler;
    this.fieldRows = form.fields;
    this.fieldsOrder = form.fieldsOrder;

    this.doUpdateFieldRows();
    this.submitButton.update({
      label: this.submitButtonLabel || 'Submit'
    });

  }

  async initialize() {

    setChildren(this.el, [el('ht-spinner', { size: 'small' })]);

    await Promise.all([this.tryCreateForm(), this.tryLoadForm()]);

    setChildren(this.el, [this.style, this.staticElementsContainer, this.dynamicElementsContainer]);

  }

  updateButtonStyles(properties) {

    const formButtonStyles = serializer.stylers.tryBuildFormButtonBackgroundStyles(properties || {}, '');
    const formButtonHoverStyles = serializer.stylers.tryBuildFormButtonHoverBackgroundStyles(properties || {}, '');

    this.style.textContent = `      @scope {
        .save-button {
          ${formButtonStyles}
        }
        .save-button:hover {
          ${formButtonHoverStyles}
        }
      }`;

  }

  update(configuration) {

    this.pageId = configuration.pageId;
    this.formId = configuration.formId;

    setAttr(this.el, {
      style: getVisualStyles(configuration.properties)
    });
    this.updateButtonStyles(configuration.properties);

  }

}

export default FormEditor;